import { Alert, AlertTitle, Box, Link, Typography } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbar,
} from "@mui/x-data-grid-pro";
import { darken, lighten, styled, Theme } from '@mui/material/styles';
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { ApiError } from "../../../app/error";
import { useAppSelector } from "../../../app/hooks";
import { routes } from "../../../app/paths";
import { ActionBanner } from "../../../common/components/functional/actionBanner/ActionBanner";
import {
  ActionButton,
  ActionButtonGroup,
} from "../../../common/components/functional/actionBanner/styled";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { canExpertChangeToNonbillable } from "../../user/accessRightChecks";
import {
  AccountingDataRow,
  examineInvoice,
  updateBillableRowState,
} from "../invoiceAPI";

type RouteParams = {
  invoiceId?: string;
  rowId?: string;
};

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles('light', {
    backgroundColor: lighten(color, coefficient),
  }),
});

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  '& .super-app-theme--voided-row': {
    ...getBackgroundColor(theme.palette.warning.main, theme, 0.7),
    '&:hover': {
      ...getBackgroundColor(theme.palette.warning.main, theme, 0.6),
    },
    '&.Mui-selected': {
      ...getBackgroundColor(theme.palette.warning.main, theme, 0.5),
      '&:hover': {
        ...getBackgroundColor(theme.palette.warning.main, theme, 0.4),
      },
    },
  },
}));

const toHeaderLabel = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (char) => char.toUpperCase());

const pattern = "yyyy-MM-dd HH:mm:ss";

const normalizeRows = (rows: AccountingDataRow[]) =>
  rows.map(({ timeRange, ...rest }) => ({
    ...rest,
    timeRangeStart: timeRange?.start ? format(timeRange.start, pattern) : null,
    timeRangeEnd: timeRange?.end ? format(timeRange.end, pattern) : null,
  }));

export const ExamineInvoicePage = () => {
  const { invoiceId, rowId } = useParams<RouteParams>();
  const [rows, setRows] = useState<AccountingDataRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Record<string, string> | null>(null);
  const { t } = useTranslation("invoices");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const user = useAppSelector((state) => state.user.user);
  const canChangeToNonbillable = user
    ? canExpertChangeToNonbillable(user)
    : false;

  useEffect(() => {
    if (!invoiceId) return;

    setIsLoading(true);
    setError(null);

    examineInvoice(invoiceId, rowId)
      .then((response) => {
        setRows(response.data.data ?? []);
      })
      .catch((err: Error) => {
        setError({ message: err.message || "Failed to load invoice data." });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [invoiceId, rowId]);

  const normalizedRows = useMemo(() => normalizeRows(rows), [rows]);
  const hasSelectedRows = rowSelectionModel.length > 0;

  const handleChangeToNonbillable = async () => {
    if (!hasSelectedRows) return;

    setIsLoading(true);
    setError(null);

    try {
      const selectedIds = rowSelectionModel.map((id) => String(id));
      await updateBillableRowState(selectedIds, false);
      const selectedIdSet = new Set(selectedIds);
      setRows((prevRows) =>
        prevRows.filter((row) => !selectedIdSet.has(String(row.id))),
      );
      setRowSelectionModel([]);
    } catch (err) {
      const errorContents =
        err instanceof ApiError
          ? {
            message: err.message,
            ...(err.context && {
              context: Object.entries(err.context)
                .map(([k, v]) => `"${k}" ${v}`)
                .join(",\n"),
            }),
          }
          : { message: "Failed to update invoice row state." };
      setError(errorContents);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo<GridColDef[]>(() => {
    if (normalizedRows.length === 0) return [];

    const orderedKeys = [
      "id",
      "type",
      "version",
      "expenseTypeCode",
      "date",
      "timeRangeStart",
      "timeRangeEnd",
      "expense",
      "amount",
      "unit",
      "eezyCompanyId",
      "eezyCostCenterId",
      "employmentId",
      "talentId",
      "tesId",
      "salaryPeriodClassId",
      "customerOrganizationId",
      "customerCostCenterId",
      "assignmentId",
      "workTitleId",
      "workLocationId",
      "contractId",
      "salesPersonId",
      "entryId",
      "expenseRowId",
      "isMonthly",
      "isShift",
      "isExpressOrder",
      "isSelfServiceOrder",
      "isEarlyBirdOrder",
      "isCustomerRecruit",
      "isBillable",
      "isPayable",
      "unitCost",
      "unitPriceOverride",
      "invoicePriceMultiplier",
      "invoiceUnitPrice",
      "invoiceAmount",
      "vatRate",
      "vatAmount",
      "invoiceId",
      "payslipId",
      "accountDebit",
      "accountCredit",
      "accountSales",
      "voided",
    ];

    const [sampleRow] = normalizedRows;

    return orderedKeys.map((key) => {
      const sampleValue = (sampleRow as Record<string, unknown>)[key];
      const valueType =
        typeof sampleValue === "number"
          ? "number"
          : typeof sampleValue === "boolean"
            ? "boolean"
            : "string";
      return {
        field: key,
        headerName: toHeaderLabel(key),
        flex: 1,
        minWidth: 140,
        type: valueType,
        ...(key === "talentId" && {
          renderCell: (params) =>
            params.value ? (
              <Link
                component={RouterLink}
                to={routes.talentProfile(String(params.value))}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {String(params.value)}
              </Link>
            ) : (
              ""
            ),
        }),
        ...(key === "employmentId" && {
          renderCell: (params) => {
            const talentId = (params.row as AccountingDataRow).talentId;
            if (!params.value || !talentId) return "";
            return (
              <Link
                component={RouterLink}
                to={routes.talentEmployment(talentId, String(params.value))}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {String(params.value)}
              </Link>
            );
          },
        }),
        ...(key === "entryId" && {
          renderCell: (params) => {
            const talentId = (params.row as AccountingDataRow).talentId;
            if (!params.value || !talentId) return "";
            return (
              <Link
                component={RouterLink}
                to={routes.talentEntries(talentId, String(params.value))}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {String(params.value)}
              </Link>
            );
          },
        }),
      };
    });
  }, [normalizedRows]);

  return (
    <Box sx={{ height: "100%", padding: 3 }}>
      <OverlaySpinner isLoading={isLoading} />
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t("invoiceAccountingRows")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("invoiceId", { ns: "settings" })}: {invoiceId}
          {rowId ? ` • ${t("invoiceRowId")}: ${rowId}` : ""}
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            marginBottom: 2,
            whiteSpace: "pre-line",
            maxWidth: "98vw",
          }}
          onClose={() => setError(null)}
        >
          <AlertTitle>{error.message}</AlertTitle>
          {error.context || ""}
        </Alert>
      )}

      <StyledDataGrid
        autoHeight
        checkboxSelection={canChangeToNonbillable}
        disableRowSelectionOnClick
        rows={normalizedRows}
        columns={columns}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
          density: "compact",
        }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            csvOptions: {
              escapeFormulas: false,
            },
            printOptions: { disableToolbarButton: true },
          },
        }}
        isRowSelectable={(params: GridRowParams) =>
          canChangeToNonbillable &&
          params.row.type === "EmploymentRow" &&
          !params.row.invoiceId
        }
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        getRowClassName={(params) => `super-app-theme--${params.row.voided ? 'voided-row' : 'normal-row'}`}
      />
      {canChangeToNonbillable && (
        <ActionBanner
          isOpen={hasSelectedRows}
          sx={{
            position: "sticky",
            bottom: 0,
            marginLeft: 0,
            width: "100%",
          }}
        >
          <ActionButtonGroup>
            <ActionButton
              variant="outlined"
              disabled={!hasSelectedRows}
              onClick={handleChangeToNonbillable}
            >
              {t("changeToNonbillable")}
            </ActionButton>
          </ActionButtonGroup>
        </ActionBanner>
      )}
    </Box>
  );
};
